import { setRequestLocale } from '@brightdrop/auth-client';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import {
  LocalizationProvider,
  type PreferenceInput,
  useFormat,
} from '@brightdrop/localization-core';
import { type PropsWithChildren, useEffect, useRef } from 'react';

import { API_CLIENTS } from '~/app/apiClients';
import { useAppDispatch } from '~/app/store';

import { LOCALIZATION_CORE_FLAG } from '../models/featureFlags.model';

/**
 * Tells the transport layer which locale to ask the platform to answer in.
 *
 * The resolved locale only exists below the provider, and the code that needs it — nine
 * `prepareHeaders` functions and an Axios interceptor — runs outside React entirely. So it is
 * carried across as a plain string through a holder in `@brightdrop/auth-client`, the one package
 * every base query shares as a federation singleton. A string rather than the core itself, because
 * `packages/rtk-clients` is bundled by an app that does not share the core and would get a second
 * copy of it.
 *
 * `useFormat().locale()` answers correctly on both sides of the rollout flag — the flag-off path
 * runs through `inertPreferencesFor`, which resolves to `en-US` — so the header agrees with the
 * `locale` body field `useServerParams` sends, and there is nothing here to branch on.
 */
const LocalePublisher = ({ children }: PropsWithChildren): JSX.Element => {
  const locale = useFormat().locale();
  const dispatch = useAppDispatch();
  const published = useRef(locale);

  // During render rather than in an effect. A child's effects run before its parents', so a screen
  // that fetches in its first effect would beat a publish made in one of ours and ask in the
  // default locale. The call is idempotent, so a render React discards leaves nothing behind that
  // the next one does not overwrite — the same reasoning the core's own snapshot publish uses.
  setRequestLocale(locale);

  // RTK Query keys its cache on endpoint and arguments and never on headers, so a response
  // fetched under one locale would be served under the next. This is not merely defensive: the
  // reader's locale arrives with the `/me` payload, so a reader who is not `en-US` changes locale
  // once on every page load, after whatever their first screen already asked for.
  useEffect(() => {
    if (published.current === locale) return;

    published.current = locale;
    for (const client of API_CLIENTS) {
      dispatch(client.util.resetApiState());
    }
  }, [dispatch, locale]);

  return <>{children}</>;
};

type LocalizationGateProps = {
  /** The profile payload, passed through to the provider unchanged. */
  preferences: PreferenceInput;
};

/**
 * Mounts the localization core with the rollout flag's answer.
 *
 * The core is zero-dependency by construction, so it cannot read LaunchDarkly itself; something on
 * the app side has to evaluate the flag and hand over the boolean. That is all this is, and keeping
 * it to that is deliberate — the provider decides what "off" means, and this decides nothing.
 *
 * It has to sit below `FeatureFlagsProvider`, which is why the shell's provider order changed.
 * Rendering the provider either way rather than mounting it conditionally means flipping the flag
 * at runtime re-renders the tree instead of remounting it, so a user watching a screen sees the
 * values change without losing what they had open.
 */
const LocalizationGate = ({
  preferences,
  children,
}: PropsWithChildren<LocalizationGateProps>): JSX.Element => {
  const { getFlag } = useFeatureFlags();

  return (
    <LocalizationProvider
      preferences={preferences}
      enabled={Boolean(getFlag(LOCALIZATION_CORE_FLAG))}
    >
      <LocalePublisher>{children}</LocalePublisher>
    </LocalizationProvider>
  );
};

export default LocalizationGate;
